import cx from 'classnames';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DrawerBottomSheet} from '~/CreateFabricInvitation/components/DrawerBottomSheet/DrawerBottomSheet';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectEditingMobile} from '~/common/fabric/reducers/fabricDesign/selectors';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';

import './EditorContainer.sass';

interface TabProps {
  title: string;
  children: React.ReactNode;
  showEnvelopeFront?: boolean;
}

interface EditorTabProps {
  title: string | undefined;
  isActive: boolean;
  onClick: () => void;
  index: number;
}

const EditorTab: React.FC<EditorTabProps> = ({title, isActive, onClick, index}) => (
  <Button
    data-qa-id={`design-editor-tab-${index}`}
    variant="unstyled-button"
    className={cx('mobile-editor__tab', {
      'mobile-editor__tab--active': isActive,
    })}
    onClick={onClick}
  >
    <Typography
      variant="label2"
      className={cx('mobile-editor__tab__label', {
        'mobile-editor__tab__label--active': isActive,
      })}
      color={isActive ? 'charcoal' : 'pebble'}
    >
      {title || `Tab ${index + 1}`}
    </Typography>
  </Button>
);

interface EditorContainerProps {
  children: React.ReactNode;
  flipEnvelope?: (showFront: boolean) => void;
  scrolls?: boolean;
}

export const Tab: React.FC<TabProps> = ({children}) => children;

export const EditorContainer: React.FC<EditorContainerProps> & {
  // biome-ignore lint/style/useNamingConvention: no pascal case
  Tab: React.FC<TabProps>;
} = ({children, flipEnvelope, scrolls}) => {
  const editingMobile = useSelector(selectEditingMobile);
  const dispatch = useDispatch();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const mobileWrapperRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    dispatch({
      type: UPDATE_DESIGN_OPTIONS,
      payload: {editingMobile: false},
    });
  }, [dispatch]);

  const childrenArray = React.Children.toArray(children).filter(
    (child) => React.isValidElement(child) && child.type === Tab
  ) as React.ReactElement<TabProps>[];

  const handleTabChange = useCallback(
    (index: number) => {
      setActiveTabIndex(index);
      if (flipEnvelope && React.isValidElement(childrenArray[index])) {
        const tabProps = childrenArray[index].props;
        flipEnvelope(!!tabProps.showEnvelopeFront);
      }
    },
    [childrenArray, flipEnvelope]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    handleTabChange(0);
  }, []);

  const renderHeader = () => (
    <div className="mobile-editor__header">
      <div className="mobile-editor__tabs">
        {childrenArray.map((tab, index) => (
          <EditorTab
            key={tab.props.title || index}
            title={tab.props.title}
            isActive={activeTabIndex === index}
            onClick={() => handleTabChange(index)}
            index={index}
          />
        ))}
      </div>
      <Button
        variant="transparent"
        className="mobile-editor__close_button"
        onClick={handleClose}
        data-qa-id="design-editor-close"
        type="button"
      >
        <CloseLineIcon ratio={0.75} />
      </Button>
    </div>
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (mobileWrapperRef.current) mobileWrapperRef.current.scrollTop = 0;
  }, [activeTabIndex]);

  if (!editingMobile) {
    return null;
  }

  return (
    <DrawerBottomSheet
      isOpen={editingMobile}
      onClose={handleClose}
      className="mobile-editor"
      header={renderHeader()}
      minHeightPercentage={30}
      maxHeightPercentage={70}
      initHeight={null}
    >
      <div
        className={cx('mobile-editor__content', {
          'mobile-editor__content--scrolls': scrolls,
        })}
        ref={mobileWrapperRef}
      >
        {childrenArray[activeTabIndex]}
      </div>
    </DrawerBottomSheet>
  );
};

EditorContainer.Tab = Tab;
